// pages/gantt/gantt.component.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { showGanttSettings } from "../../redux/settings/settings.actions";
import { useActiveSpaceData } from "../../hooks/useActiveSpaceData.hook";
import { db, fieldValue } from "../../firebase/firebase.utils";
import GanttChart from "../../components/gantt/Gantt";
import Gantt from "../../components/gantt/frappe-gantt";
import { tasksSample } from "./gantt.sampledata";
import AddButton from "../../components/task-add-btn/AddButton";
import BoxLayer from "../../components/retro/box-layer/box-layer.component";
import {
  getPriorityOptions,
  normalizePriorityValue,
} from "../../utils/priorities";
import InvitetoPremium from "../../components/marketing/invite-to-premium.component";
import {
  BsLink45Deg,
  BsCalendar2Week,
  BsCalendar2WeekFill,
  BsPercent,
  BsListNested,
  BsLayoutSidebar,
  BsLayoutSidebarInset,
  BsChevronBarDown,
  BsChevronDown,
} from "react-icons/bs";
import { IoArrowUp, IoArrowDown } from "react-icons/io5";
import "./gantt.styles.scss";
const GanttView = ({ station, stationTasks, stationTasksFL }) => {
  const users = useSelector((state) => state.user.users);
  const open = useSelector((state) => state.user.currentUser.open);
  const userEntitlement = useSelector((state) => state.user.entitlement);
  const phases = useSelector((state) => state.prjphases.prjphases);
  const filter = useSelector((state) => state.filter);
  const settingsOn = useSelector((state) => state.settings.showGanttSettings);
  const dispatch = useDispatch();
  const location = useLocation();
  const currentSpaceId = location.pathname.split("/")[2];
  const currentStationId = location.pathname.split("/")[4];
  const stationDocument = Array.isArray(station)
    ? station.find((item) => (item?.stationId ?? item?.id) === currentStationId)
    : station;
  const projectPriorities = getPriorityOptions(stationDocument?.priorities);
  const getTaskPriorityName = (task) => {
    // Temporary compatibility for old tasks
    if (Array.isArray(task?.priority)) {
      return (
        task.priority.find((priority) => priority?.active === true)?.name ||
        "No priority"
      );
    }
    // New numerical model
    const priorityValue = normalizePriorityValue(task?.priority);
    return (
      projectPriorities.find((priority) => priority.value === priorityValue)
        ?.name || `Priority ${priorityValue}`
    );
  };
  const [state, setState] = useState([]);
  // const [state, setState] = useState([]);
  const spaceData = useActiveSpaceData();
  const [viewMode, setViewMode] = useState("Day");
  const currentUser = useSelector((state) => state.user.currentUser);
  const [showInvitation, setShowInvitation] = useState(true);
  const [showSwimlane, setShowSwimlane] = useState(true);
  const [showWeekends, setShowWeekends] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [showGroupBy, setShowGroupBy] = useState(false);
  const [groupBy, setGroupBy] = useState("phase");
  const [showSortBy, setShowSortBy] = useState(false);
  const [sortBy, setSortBy] = useState("phase");
  const navigate = useNavigate();
  const _svg = useRef();
  const _chart = useRef();
  const ganttHostRef = useRef();
  const stickyFrameRef = useRef(null);
  useEffect(() => {
    setState(station);
  }, [station]);
  const [showSettings, setShowSettings] = useState(false);
  useEffect(() => {
    if (settingsOn) {
      setShowSettings(true);
    } else {
      setShowSettings(false);
    }
  }, [settingsOn]);
  const handleCloseSettings = () => {
    dispatch(showGanttSettings());
  };
  const [tasks, setTasklist] = useState([]);
  const [samplemode, setSampleMode] = useState(true);
  const transformTasks = async () => {
    if (!stationTasksFL.empty) {
      const array = await stationTasksFL
        .filter((task) => task.startdate !== null)
        .sort((a, b) => (a.startdate > b.startdate ? 1 : -1))
        .map((task) => {
          const phaseRefName = phases?.find(
            (phase) => phase.id === task.phase,
          )?.name;
          const assigneeName = users?.find(
            (user) => user.uid === task.assign,
          )?.userName;
          const priorityName = getTaskPriorityName(task);
          const selectedswimlane =
            groupBy === "phase"
              ? phaseRefName
              : groupBy === "assignee"
                ? assigneeName
                : priorityName;
          return {
            start: task.startdate?.toDate(),
            end: task.enddate?.toDate(),
            name: task.title,
            id: task.id,
            progress: task.progress,
            swimlane: selectedswimlane,
            // sub_swimlane: doc.get("id"),
            // sub_swimlane: phaseRefName,
            editable: true,
            start_drag: true,
            end_drag: true,
            custom_class: task.type,
            dependencies: task.dependencies,
          };
        });
      setTasklist([...array]);
      if (!tasks.length) {
        _chart.current?.refresh([...array]);
        setTimeout(() => {
          handleScrollToToday({ behavior: "auto" });
          updateStickyGanttHeader();
        }, 0);
      }
      setSampleMode(false);
    } else {
      setTasklist(tasksSample);
    }
  };
  useEffect(() => {
    transformTasks();
  }, [stationTasksFL, stationDocument?.priorities, phases, users]);
  const transformTasksByGroup = async () => {
    if (!stationTasksFL.empty) {
      const array = await stationTasksFL
        .filter((task) => task.startdate !== null)
        .sort((a, b) => (a.startdate > b.startdate ? 1 : -1))
        .map((task) => {
          const phaseRefName = phases?.find(
            (phase) => phase.id === task.phase,
          )?.name;
          const assigneeName = users?.find(
            (user) => user.uid === task.assign,
          )?.userName;
          const priorityName = getTaskPriorityName(task);
          const selectedswimlane =
            groupBy === "phase"
              ? phaseRefName
              : groupBy === "assignee"
                ? assigneeName
                : priorityName;
          return {
            start: task.startdate?.toDate(),
            end: task.enddate?.toDate(),
            name: task.title,
            id: task.id,
            progress: task.progress,
            swimlane: selectedswimlane,
            // sub_swimlane: doc.get("id"),
            // sub_swimlane: phaseRefName,
            editable: true,
            start_drag: true,
            end_drag: true,
            custom_class: task.type,
            dependencies: task.dependencies,
          };
        });
      setTasklist([...array]);
      const currentScrollLeft = ganttHostRef.current?.scrollLeft || 0;
      _chart.current?.refresh([...array]);
      setTimeout(() => {
        if (ganttHostRef.current) {
          ganttHostRef.current.scrollLeft = currentScrollLeft;
        }
        updateStickyGanttHeader();
      }, 0);
      setSampleMode(false);
    } else {
      setTasklist(tasksSample);
    }
  };
  useEffect(() => {
    transformTasksByGroup();
  }, [groupBy]);
  const handleTaskChange = (task, start, end) => {
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("tasks")
      .doc(task.id)
      .update({
        startdate: start,
        enddate: end,
      });
  };
  const handleDependencyAdd = (connection) => {
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("tasks")
      .doc(connection.to)
      .update({
        dependencies: fieldValue.arrayUnion(connection.from),
      });
  };
  const handleDependencyRemove = (connection) => {
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("tasks")
      .doc(connection.to)
      .update({
        dependencies: fieldValue.arrayRemove(connection.from),
      });
  };
  const handleProgressChange = async (task) => {
    // setTasks(tasks.map((t) => (t.id === task.id ? task : t)));
    // console.log("On progress change Id:" + task.id);
    db.collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("tasks")
      .doc(task.id)
      .update({
        progress: task.progress,
      });
  };
  const handleViewModeChange = (e) => {
    const nextMode = e.target.value;
    setViewMode(nextMode);
    _chart.current?.change_view_mode(nextMode);
    setTimeout(() => {
      handleScrollToToday({ behavior: "auto" });
      updateStickyGanttHeader();
    }, 0);
  };
  const handleOpenTask = (event) => {
    // setViewMode(e.target.value);
    // _chart.current.change_view_mode(e.target.value);
    navigate(`task/${event.id}`);
    _chart.current?.update();
  };
  const customPopupHtml = (task) => {
    const end_date = task._end.format("MMM D");
    this.setState({ editTask: "open" });
    return `
      <div class="details-container">
        <h5>${task.name}</h5>
        <h5>${task.start}</h5>
        <h5>${task.progress}% completed!</h5>
        <a href="google.com">LINK!</a>
        <p>6545</p>
        <p>564</p>
      </div>
    `;
  };
  function onViewChange(mode) {
    console.log(mode);
  }
  function onProgressChange(task, progress) {
    console.log(task, progress);
  }
  function onClick(task) {
    console.log(task);
  }
  function onDateChange(task, start, end) {
    console.log(task, start, end);
    // handleTaskChange(task, start, end);
  }
  useEffect(() => {
    if (_chart.current) return;
    _chart.current = new Gantt(_svg.current, tasks, {
      on_click: function (task) {
        console.log(task);
        handleOpenTask(task);
      },
      on_dependency_added: function (connection) {
        console.log(connection);
        handleDependencyAdd(connection);
      },
      on_dependency_deleted: function (connection) {
        console.log(connection);
        handleDependencyRemove(connection);
      },
      on_date_change: function (task, start, end) {
        console.log(task, start, end);
        handleTaskChange(task, start, end);
      },
      on_progress_change: function (task, progress) {
        console.log(task, progress);
        handleProgressChange(task);
      },
      on_view_change: function (mode) {
        console.log(mode);
      },
      view_mode: viewMode,
      show_weekends: showWeekends,
      // popup_trigger: "mouseover",
    });
  }, []);
  const handleScrollToToday = ({ behavior }) => {
    const host = ganttHostRef.current;
    const chart = _chart.current;
    if (!host || !chart?.gantt_start) return;
    const now = new Date();
    const diffHours =
      (now.getTime() - chart.gantt_start.getTime()) / (1000 * 60 * 60);
    const nowX = (diffHours / chart.options.step) * chart.options.column_width;
    host.scrollTo({
      left: Math.max(nowX - host.clientWidth / 2, 0),
      behavior: behavior !== undefined ? behavior : "smooth",
    });
  };
  useEffect(() => {
    const host = ganttHostRef.current;
    if (!host) return;
    const onScroll = () => {
      updateStickyGanttHeader();
    };
    host.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      host.removeEventListener("scroll", onScroll);
      if (stickyFrameRef.current) {
        cancelAnimationFrame(stickyFrameRef.current);
      }
    };
  }, []);
  const updateStickyGanttHeader = () => {
    if (stickyFrameRef.current) return;
    stickyFrameRef.current = requestAnimationFrame(() => {
      stickyFrameRef.current = null;
      const host = ganttHostRef.current;
      const chart = _chart.current;
      if (!host || !chart?.$svg) return;
      const scrollTop = host.scrollTop || 0;
      const stickyLayer = chart.$svg.querySelector(".sticky-header-layer");
      if (stickyLayer) {
        stickyLayer.setAttribute("transform", `translate(0, ${scrollTop})`);
      }
    });
  };
  return (
    <div className="ganttView" style={{ marginLeft: open ? 0 : 10 }}>
      <div
        className={`ganttContainer ${!showSwimlane && "noSwimlane"} ${
          !showWeekends && "noWeekends"
        }`}
      >
        {currentUser.plan === "Free" ? (
          <div onClick={() => setShowInvitation(!showInvitation)}>
            {showInvitation && (
              <div>
                <InvitetoPremium
                  setShowInvitation={setShowInvitation}
                  featureName={"Gantt View"}
                  featureDesc={
                    "arrange your tasks & milestones with ease, link them with dependencies, see the big picture"
                  }
                  featureImg={
                    "https\://images.unsplash.com/photo-1577563682708-4f022ec774fb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2167&q=80"
                  }
                  featureImgStyle={{ transform: "scaleX(-1)" }}
                  currentUser={currentUser}
                />
              </div>
            )}
            <div className="gantt__upperrow">
              <h4>
                This is just how a sample chart looks like... When you change a
                task or event's date, all the related task/event dates to be
                automatically updated for you. So updating your goals,
                milestones or complex project plan is easy-peasy 🙂 To upgrade
                now, visit{" "}
                <a href="https\://antlogy.com/main/#pricing" target="_blank">
                  {" "}
                  here.
                  <BsLink45Deg size="1.4em" />
                </a>
                <br />
                <br />
                Additionally for our Premium or Business Plan users; 'Month',
                'Week', 'Day', '12 hours', '6 hours' views are supported in the
                timeline...
              </h4>
            </div>
            <GanttChart
              tasks={tasksSample}
              viewMode={viewMode}
              onDateChange={(task, start, end) =>
                handleTaskChange(task, start, end)
              }
              // handleProgressChange={handleProgressChange}
            />
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", height: "100%", rowGap: "6px" }}
          >
            <div className="gantt__upperrow">
              {!samplemode && (
                <div className="gantt__groupby">
                  <button
                    onClick={() => setShowGroupBy(!showGroupBy)}
                    style={{ textWrapMode: "nowrap", fontWeight: 600 }}
                  >
                    Group by
                  </button>
                  <button
                    className="gantt__groupbyBtn"
                    onClick={() => setShowGroupBy(!showGroupBy)}
                  >
                    <p>{groupBy ? groupBy : "none"}</p>
                    <BsChevronDown />
                  </button>
                  {showGroupBy && (
                    <div className="chat_grpselect">
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setShowSwimlane(true) &
                          setGroupBy("phase") &
                          setShowGroupBy(false)
                        }
                      >
                        Phase
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setShowSwimlane(true) &
                          setGroupBy("assignee") &
                          setShowGroupBy(false)
                        }
                      >
                        Assignee
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setShowSwimlane(true) &
                          setGroupBy("priority") &
                          setShowGroupBy(false)
                        }
                      >
                        Priority
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setShowSwimlane(false) &
                          setGroupBy(null) &
                          setShowGroupBy(false)
                        }
                      >
                        Don't group
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!samplemode && (
                <div className="gantt__groupby">
                  <button
                    onClick={() => setShowSortBy(!showSortBy)}
                    style={{ textWrapMode: "nowrap", fontWeight: 600 }}
                  >
                    Sort by
                  </button>
                  <button
                    className="gantt__groupbyBtn"
                    onClick={() => setShowSortBy(!showSortBy)}
                  >
                    <p>{sortBy ? sortBy : "none"}</p>
                    <BsChevronDown />
                  </button>
                  {showSortBy && (
                    <div className="chat_sortselect">
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setSortBy("createdAt") & setShowSortBy(false)
                        }
                      >
                        Created at
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setSortBy("startDate") & setShowSortBy(false)
                        }
                      >
                        Start date
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setSortBy("endDate") & setShowSortBy(false)
                        }
                      >
                        End date
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setSortBy("alphabetical") & setShowSortBy(false)
                        }
                      >
                        Alphabetical
                      </button>
                      <button
                        className="chat_grpoption"
                        onClick={() =>
                          setSortBy("priority") & setShowSortBy(false)
                        }
                      >
                        Priority
                      </button>
                      <div className="sort_direction-row">
                        <button
                          className="chat_grpoption"
                          onClick={() =>
                            setSortBy("priority") & setShowSortBy(false)
                          }
                        >
                          Asc
                          <IoArrowUp />
                        </button>
                        <button
                          className="chat_grpoption"
                          onClick={() =>
                            setSortBy("priority") & setShowSortBy(false)
                          }
                        >
                          Desc
                          <IoArrowDown />
                        </button>
                      </div>
                      <div className="sort_direction-row">
                        <button className="chat_grpoption">
                          Hide completed
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {samplemode && (
                <h4>
                  You don't have any tasks with dates yet. Until you add some,
                  this is a sample timeline for you to try out this feature...
                </h4>
              )}
              <div className="gantt__buttons">
                <button
                  className="gantt__button-now"
                  onClick={() => handleScrollToToday({ behavior: "smooth" })}
                  type="button"
                >
                  Now
                </button>
                <div className="gantt__buttongroup">
                  <button
                    style={{
                      textDecoration:
                        viewMode === "Quarter Day" &&
                        `underline 2px ${spaceData?.color}`,
                    }}
                    className={
                      viewMode === "Quarter Day"
                        ? "gantt__button-active"
                        : "gantt__button"
                    }
                    value="Quarter Day"
                    onClick={handleViewModeChange}
                  >
                    6hours
                  </button>
                  <button
                    style={{
                      textDecoration:
                        viewMode === "Half Day" &&
                        `underline 2px ${spaceData?.color}`,
                    }}
                    className={
                      viewMode === "Half Day"
                        ? "gantt__button-active"
                        : "gantt__button"
                    }
                    value="Half Day"
                    onClick={handleViewModeChange}
                  >
                    12hours
                  </button>
                </div>
                <button
                  style={{
                    textDecoration:
                      viewMode === "Day" && `underline 2px ${spaceData?.color}`,
                  }}
                  className={
                    viewMode === "Day"
                      ? "gantt__button-active"
                      : "gantt__button"
                  }
                  value="Day"
                  onClick={handleViewModeChange}
                >
                  Day
                </button>
                <div className="gantt__buttongroup">
                  <button
                    style={{
                      textDecoration:
                        viewMode === "Week" &&
                        `underline 2px ${spaceData?.color}`,
                    }}
                    className={
                      viewMode === "Week"
                        ? "gantt__button-active"
                        : "gantt__button"
                    }
                    value="Week"
                    onClick={handleViewModeChange}
                  >
                    Week
                  </button>
                  <button
                    style={{
                      textDecoration:
                        viewMode === "Month" &&
                        `underline 2px ${spaceData?.color}`,
                    }}
                    className={
                      viewMode === "Month"
                        ? "gantt__button-active"
                        : "gantt__button"
                    }
                    value="Month"
                    onClick={handleViewModeChange}
                  >
                    Month
                  </button>
                  {/* <button
              className={
                viewMode === "Year"
                  ? "gantt__button-active"
                  : "gantt__button"
              }
              onClick={() => _chart.current.change_view_mode("Year")}
            >
              Year
            </button> */}
                </div>
              </div>
            </div>
            {/* <GanttChart
            tasks={tasklist}
            viewMode={viewMode}
            onDateChange={(task, start, end) =>
              handleTaskChange(task, start, end)
            }
            handleProgressChange={handleProgressChange}
            // onProgressChange={handleProgressChange}
          /> */}
            <div className="gantt-host" ref={ganttHostRef}>
              <svg
                ref={_svg}
                // style={{ display: "flex", height: "calc(100vh - 250px)" }}
                xmlns="http\://www.w3.org/2000/svg"
                xmlnsXlink="http\://www.w3.org/1999/xlink"
              />
            </div>
          </div>
        )}
        <div className="gantt__settings__win">
          {showSettings && (
            <BoxLayer setLayer={handleCloseSettings}>
              <div className="gantt__buttongroup-settings">
                <button onClick={() => setShowSwimlane(!showSwimlane)}>
                  {showSwimlane ? (
                    <BsLayoutSidebarInset />
                  ) : (
                    <BsLayoutSidebar />
                  )}
                  Show Status
                </button>
                <button onClick={() => setShowWeekends(!showWeekends)}>
                  {showWeekends ? <BsCalendar2WeekFill /> : <BsCalendar2Week />}{" "}
                  Show Weekends
                </button>
                <button onClick={() => setShowProgress(!showProgress)}>
                  {showProgress ? <BsPercent /> : <BsListNested />} Show
                  Progress
                </button>
                <div className="gantt__switch"></div>
              </div>
            </BoxLayer>
          )}
        </div>
      </div>
      <Outlet />
      <AddButton />
    </div>
  );
};
export default GanttView;
